import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, CalendarPlus } from "lucide-react";
import api from "@/lib/api";
import { formatDateRange, formatTimeRange, todayLocalISO } from "@/lib/formatDate";
import { tzAbbrev } from "@/lib/timezone";

const GREEN = "#22c55e";

function sortEvents(items, field, dir) {
    const val = (x) => (field === "name" ? (x.name || "").toLowerCase() : (x.event_date || ""));
    const s = [...items].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0));
    return dir === "desc" ? s.reverse() : s;
}

// Events for an entity, shown in the homepage "Upcoming Events" row style.
// `params` selects which events (entity_owner / fan_creator / mention).
// `canClaim` shows a one-tap "Claim it" button (owner viewing their own page).
// Renders nothing if there are no events in the current (upcoming/past) view.
export default function EntityEvents({ params, title = "Upcoming Events", canClaim = false, refreshToken = 0, onChanged, greenHeading = false, addEventLink = "", manageTeamId = "", manageTrainerId = "" }) {
    const [events, setEvents] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [when, setWhen] = useState("upcoming");
    const [sort, setSort] = useState("date");
    const [dir, setDir] = useState("asc");
    const [pageSize, setPageSize] = useState(5);
    const [page, setPage] = useState(0);
    const [claiming, setClaiming] = useState(null);
    const [linkBusy, setLinkBusy] = useState(null);
    const [bump, setBump] = useState(0);

    const key = JSON.stringify(params || {});
    useEffect(() => {
        const hasFilter = params && (params.entity_owner || params.fan_creator || params.created_by || params.mention || params.team || params.trainer);
        if (!hasFilter) { setLoaded(true); return; }
        let active = true;
        setLoaded(false);
        api.get("/events", { params: { ...params, when: "all", ...(manageTeamId ? { manage_team: manageTeamId } : {}), ...(manageTrainerId ? { manage_trainer: manageTrainerId } : {}) } })
            .then(({ data }) => { if (active) { setEvents(data || []); setLoaded(true); } })
            .catch(() => { if (active) { setEvents([]); setLoaded(true); } });
        return () => { active = false; };
    }, [key, refreshToken, bump, manageTeamId, manageTrainerId]);

    const manageId = manageTeamId || manageTrainerId;
    const isTrainerManage = !!manageTrainerId;
    const toggleLink = async (e, ev) => {
        e.preventDefault();
        e.stopPropagation();
        if (linkBusy) return;
        const link = !ev.is_linked;
        const path = isTrainerManage ? (link ? "link-trainer" : "unlink-trainer") : (link ? "link-team" : "unlink-team");
        const body = isTrainerManage ? { trainer_id: manageId } : { team_id: manageId };
        setLinkBusy(ev.id);
        try {
            await api.post(`/events/${ev.id}/${path}`, body);
            toast.success(link ? `Linked “${ev.name}”` : `Unlinked “${ev.name}”`);
            setBump((b) => b + 1);
            if (onChanged) onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update link");
        } finally {
            setLinkBusy(null);
        }
    };

    const claim = async (e, ev) => {
        e.preventDefault();
        e.stopPropagation();
        if (claiming) return;
        setClaiming(ev.id);
        try {
            await api.post(`/events/${ev.id}/claim-owned`);
            toast.success(`Claimed "${ev.name}" — moved to your events`);
            setEvents((list) => list.filter((x) => x.id !== ev.id));
            if (onChanged) onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not claim event");
        } finally {
            setClaiming(null);
        }
    };

    const today = todayLocalISO();
    const effEnd = (e) => e.end_date || e.event_date || "";
    const upcomingAll = events.filter((e) => effEnd(e) >= today);
    const view = when === "past" ? events.filter((e) => effEnd(e) < today) : upcomingAll;

    // Hide the section entirely when the owner has no events at all (keeps pages tidy) —
    // unless the owner can add a linked event here, in which case we keep the header+button.
    if (!loaded) return null;
    const hasEvents = events.length > 0;
    if (!hasEvents && !addEventLink) return null;

    const sorted = sortEvents(view, sort, dir);
    const paged = sorted.slice(page * pageSize, page * pageSize + pageSize);
    const pages = Math.ceil(view.length / pageSize);
    const heading = when === "past" ? title.replace(/^Upcoming/i, "Past") : title;

    return (
        <div className="mt-8" data-testid="entity-events">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <h2 className={`font-display text-2xl tracking-wide flex items-center gap-2.5 ${greenHeading ? "text-[#22c55e]" : ""}`}>
                    {greenHeading && <Calendar className="h-6 w-6 shrink-0 text-[#22c55e]" />}
                    {heading}
                </h2>
                {addEventLink && (
                    <Link to={addEventLink} data-testid="entity-events-add-linked" className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#22c55e] text-[10px] uppercase tracking-[0.2em] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors">
                        <CalendarPlus className="h-3.5 w-3.5" /> Add linked event
                    </Link>
                )}
            </div>
            {!hasEvents ? (
                <div className="border border-[#2a2a2a] py-8 text-center text-sm text-[#a0a0a0]" data-testid="entity-events-none">No events yet — use “Add linked event” to create one.</div>
            ) : (
            <>
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="entity-events-controls">
                <div className="inline-flex border border-[#2a2a2a]">
                    <button type="button" data-testid="entity-events-upcoming" onClick={() => { setWhen("upcoming"); setPage(0); }} className={`text-xs uppercase tracking-wider px-3 py-1.5 ${when === "upcoming" ? "bg-[#22c55e] text-black" : "bg-[#141414] text-[#a0a0a0]"}`}>Upcoming</button>
                    <button type="button" data-testid="entity-events-past" onClick={() => { setWhen("past"); setPage(0); }} className={`text-xs uppercase tracking-wider px-3 py-1.5 ${when === "past" ? "bg-[#22c55e] text-black" : "bg-[#141414] text-[#a0a0a0]"}`}>Past</button>
                </div>
                <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }} data-testid="entity-events-sort" className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                    <option value="date">Sort: Date</option>
                    <option value="name">Sort: Name</option>
                </select>
                <button type="button" data-testid="entity-events-dir" onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 uppercase tracking-wider">
                    {dir === "asc" ? "▲ Asc" : "▼ Desc"}
                </button>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} data-testid="entity-events-size" className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                    {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
            </div>
            {view.length === 0 ? (
                <div className="border border-[#2a2a2a] py-8 text-center text-sm text-[#a0a0a0]" data-testid="entity-events-empty">No {when} events.</div>
            ) : (
                <div className="grid grid-cols-1 gap-3" data-testid="entity-events-grid">
                    {paged.map((e) => (
                        <div key={e.id} className="relative group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-[#2a2a2a] bg-[#141414] p-3 transition-colors" style={{ borderLeft: `3px solid ${GREEN}` }}>
                            <Link to={`/events/${e.detail_event_id || e.id}`} data-testid={`entity-event-card-${e.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                                <Thumb src={e.photo} fallback={(e.name || "?")[0]} />
                                <div className="min-w-0 flex-1">
                                    <div className="min-w-0">
                                        <span className="font-display text-xl tracking-wide break-words group-hover:text-[#22c55e]">{e.name}</span>
                                    </div>
                                    <div className="text-xs text-[#a0a0a0] truncate">
                                        {formatDateRange(e.event_date, e.end_date)}{e.start_time ? ` · ${formatTimeRange(e.start_time, e.end_time)}${tzAbbrev(e.timezone, e.event_date) ? " " + tzAbbrev(e.timezone, e.event_date) : ""}` : ""} · {e.type} · {e.city}, {e.state}
                                    </div>
                                </div>
                            </Link>
                            {canClaim && e.is_linked !== true && e.is_linked !== false && (
                                <button
                                    type="button"
                                    onClick={(ev) => claim(ev, e)}
                                    disabled={claiming === e.id}
                                    data-testid={`entity-event-claim-${e.id}`}
                                    className="w-full sm:w-auto sm:shrink-0 text-center text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-2 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors disabled:opacity-50"
                                >
                                    {claiming === e.id ? "Claiming…" : "Claim event"}
                                </button>
                            )}
                            {e.is_linked === true ? (
                                <button
                                    type="button"
                                    onClick={(evn) => toggleLink(evn, e)}
                                    disabled={linkBusy === e.id}
                                    data-testid={`entity-event-unlink-${e.id}`}
                                    className="w-full sm:w-auto sm:shrink-0 text-center text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-2 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                                >
                                    {linkBusy === e.id ? "…" : "Unlink"}
                                </button>
                            ) : e.is_linked === false ? (
                                <button
                                    type="button"
                                    onClick={(evn) => toggleLink(evn, e)}
                                    disabled={linkBusy === e.id}
                                    data-testid={`entity-event-link-${e.id}`}
                                    className="w-full sm:w-auto sm:shrink-0 text-center text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-2 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors disabled:opacity-50"
                                >
                                    {linkBusy === e.id ? "…" : "Link event"}
                                </button>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
            {pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4" data-testid="entity-events-pager">
                    <button type="button" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Prev</button>
                    <span className="text-xs text-[#a0a0a0]">Page {page + 1} / {pages}</span>
                    <button type="button" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Next</button>
                </div>
            )}
            </>
            )}
        </div>
    );
}

function Thumb({ src, fallback }) {
    const [failed, setFailed] = useState(false);
    if (src && !failed) return <img src={src} alt="" onError={() => setFailed(true)} className="h-14 w-14 object-cover border border-[#2a2a2a] shrink-0 bg-white" />;
    return <span className="h-14 w-14 shrink-0 flex items-center justify-center border border-[#2a2a2a] font-display text-lg text-[#22c55e]">{fallback}</span>;
}
